import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Database, 
  Loader2,
  X,
  CheckCircle,
  Cloud,
  Server,
  Monitor,
  ArrowLeft,
  Upload,
  Settings
} from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useNotification } from '../context/NotificationContext';
import { apiService } from '../services/apiService';
import ProcessLoadingScreen from '../components/ProcessLoadingScreen';

interface DatabaseConnection {
  type: 'mysql' | 'postgresql' | 'oracle' | 'sqlserver';
  host: string;
  port: string;
  database: string;
  username: string;
  password: string;
  schema?: string;
}

const ExportDestinationPage: React.FC = () => {
  const navigate = useNavigate();
  const { runId } = useParams<{ runId: string }>();
  const { showSuccess, showError } = useNotification();
  
  // Loading states
  const [showLoadingScreen, setShowLoadingScreen] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  
  // Database connection states
  const [dbType, setDbType] = useState<'mysql' | 'postgresql' | 'oracle' | 'sqlserver'>('mysql');
  const [dbConnection, setDbConnection] = useState<DatabaseConnection>({
    type: 'mysql',
    host: 'localhost',
    port: '3306',
    database: '',
    username: '',
    password: '',
    schema: ''
  });
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [isExporting, setIsExporting] = useState(false);
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [availableTables, setAvailableTables] = useState<string[]>([]);

  useEffect(() => {
    // Load available tables from the run
    if (runId) {
      loadAvailableTables();
    }
  }, [runId]);

  const loadAvailableTables = async () => {
    if (!runId) return;
    
    try {
      const runDetails = await apiService.getRun(runId);
      if (runDetails.synthetic_csv_files && runDetails.synthetic_csv_files.length > 0) {
        const tables = runDetails.synthetic_csv_files.map((file: string) => 
          file.replace('_synthetic.csv', '').replace('.csv', '')
        );
        setAvailableTables(tables);
        setSelectedTables(tables); // Select all by default
      }
    } catch (error) {
      console.error('Failed to load tables:', error);
    }
  };

  const handleOpenDbModal = (type: 'mysql' | 'postgresql' | 'oracle' | 'sqlserver') => {
    const defaultPorts: Record<string, string> = {
      mysql: '3306',
      postgresql: '5432',
      oracle: '1521',
      sqlserver: '1433'
    };
    
    setDbType(type);
    setDbConnection({
      type,
      host: 'localhost',
      port: defaultPorts[type],
      database: '',
      username: '',
      password: '',
      schema: ''
    });
    setConnectionStatus('idle');
  };

  const handleTestConnection = async () => {
    setConnectionStatus('testing');
    
    try {
      const response = await apiService.testDatabaseConnection(dbConnection);
      
      if (response.success) {
        setConnectionStatus('success');
        showSuccess('Connection Successful', 'Database connection test passed!');
      } else {
        setConnectionStatus('error');
        showError('Connection Failed', response.error || 'Could not connect to database');
      }
    } catch (error) {
      setConnectionStatus('error');
      showError('Connection Failed', error instanceof Error ? error.message : 'Could not connect to database');
    }
  };

  const handleExportToDatabase = async () => {
    if (!runId) return;
    
    if (!dbConnection.host || !dbConnection.database || !dbConnection.username) {
      showError('Error', 'Please fill in all required fields');
      return;
    }

    if (selectedTables.length === 0) {
      showError('Error', 'Please select at least one table to export');
      return;
    }

    setIsExporting(true);
    setShowLoadingScreen(true);
    setLoadingStep(0);

    const steps = [
      'Connecting to database',
      'Preparing export data',
      'Exporting tables',
      'Validating data',
      'Finalizing export'
    ];

    const stepInterval = setInterval(() => {
      setLoadingStep(prev => (prev + 1) % steps.length);
    }, 1500);

    try {
      const result = await apiService.exportToDatabase(runId, {
        ...dbConnection,
        tables: selectedTables
      });

      clearInterval(stepInterval);
      setShowLoadingScreen(false);

      if (result && result.success) {
        showSuccess('Export Successful', `Successfully exported ${selectedTables.length} table(s) to database`);
        // Navigate back to generate page
        navigate(`/run/${runId}/generate`);
      } else {
        showError('Export Failed', result?.error || 'Failed to export to database');
      }
    } catch (error) {
      clearInterval(stepInterval);
      setShowLoadingScreen(false);
      console.error('Failed to export to database:', error);
      showError('Error', error instanceof Error ? error.message : 'Failed to export to database');
    } finally {
      setIsExporting(false);
    }
  };

  const getDbIcon = (type: string) => {
    switch (type) {
      case 'mysql':
        return <Database className="w-6 h-6 text-orange-600" />;
      case 'postgresql':
        return <Database className="w-6 h-6 text-blue-600" />;
      case 'oracle':
        return <Database className="w-6 h-6 text-red-600" />;
      case 'sqlserver':
        return <Database className="w-6 h-6 text-blue-500" />;
      default:
        return <Database className="w-6 h-6 text-gray-600" />;
    }
  };

  const getDbName = (type: string) => {
    switch (type) {
      case 'mysql':
        return 'MySQL';
      case 'postgresql':
        return 'PostgreSQL';
      case 'oracle':
        return 'Oracle';
      case 'sqlserver':
        return 'SQL Server';
      default:
        return type;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <ProcessLoadingScreen
        isVisible={showLoadingScreen}
        title="Exporting to Database"
        description={`Exporting generated CSV files to ${getDbName(dbType)}...`}
        steps={[
          'Connecting to database',
          'Preparing export data',
          'Exporting tables',
          'Validating data',
          'Finalizing export'
        ]}
        currentStep={loadingStep}
      />

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(`/run/${runId}/generate`)}
            className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-4"
          >
            <ArrowLeft size={20} />
            <span>Back to Generate Page</span>
          </button>
          
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Export Destination
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Choose where to export your generated synthetic data
          </p>
        </div>

        {/* Connection Status Banner */}
        {connectionStatus === 'success' && (
          <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center space-x-3">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <div>
              <p className="font-semibold text-green-900 dark:text-green-100">Output Source Connected</p>
              <p className="text-sm text-green-700 dark:text-green-300">
                Successfully connected to {getDbName(dbType)} database
              </p>
            </div>
          </div>
        )}

        {/* Source Selection */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Select Output Source
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* MySQL */}
            <button
              onClick={() => handleOpenDbModal('mysql')}
              className={`p-6 border-2 rounded-lg transition-all ${
                dbType === 'mysql' && connectionStatus === 'success'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : dbType === 'mysql'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex flex-col items-center space-y-3">
                {getDbIcon('mysql')}
                <span className="font-semibold text-gray-900 dark:text-white">MySQL</span>
                {dbType === 'mysql' && connectionStatus === 'success' && (
                  <span className="text-xs text-green-600 dark:text-green-400">Connected</span>
                )}
              </div>
            </button>

            {/* PostgreSQL */}
            <button
              onClick={() => handleOpenDbModal('postgresql')}
              className={`p-6 border-2 rounded-lg transition-all ${
                dbType === 'postgresql' && connectionStatus === 'success'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : dbType === 'postgresql'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex flex-col items-center space-y-3">
                {getDbIcon('postgresql')}
                <span className="font-semibold text-gray-900 dark:text-white">PostgreSQL</span>
                {dbType === 'postgresql' && connectionStatus === 'success' && (
                  <span className="text-xs text-green-600 dark:text-green-400">Connected</span>
                )}
              </div>
            </button>

            {/* Oracle */}
            <button
              onClick={() => handleOpenDbModal('oracle')}
              className={`p-6 border-2 rounded-lg transition-all ${
                dbType === 'oracle' && connectionStatus === 'success'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : dbType === 'oracle'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex flex-col items-center space-y-3">
                {getDbIcon('oracle')}
                <span className="font-semibold text-gray-900 dark:text-white">Oracle</span>
                {dbType === 'oracle' && connectionStatus === 'success' && (
                  <span className="text-xs text-green-600 dark:text-green-400">Connected</span>
                )}
              </div>
            </button>

            {/* SQL Server */}
            <button
              onClick={() => handleOpenDbModal('sqlserver')}
              className={`p-6 border-2 rounded-lg transition-all ${
                dbType === 'sqlserver' && connectionStatus === 'success'
                  ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                  : dbType === 'sqlserver'
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex flex-col items-center space-y-3">
                {getDbIcon('sqlserver')}
                <span className="font-semibold text-gray-900 dark:text-white">SQL Server</span>
                {dbType === 'sqlserver' && connectionStatus === 'success' && (
                  <span className="text-xs text-green-600 dark:text-green-400">Connected</span>
                )}
              </div>
            </button>
          </div>
        </div>

        {/* Connection Form */}
        {dbType && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {getDbName(dbType)} Connection Details
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Host
                </label>
                <input
                  type="text"
                  value={dbConnection.host}
                  onChange={(e) => setDbConnection({ ...dbConnection, host: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="localhost"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Port
                </label>
                <input
                  type="text"
                  value={dbConnection.port}
                  onChange={(e) => setDbConnection({ ...dbConnection, port: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="3306"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Database
                </label>
                <input
                  type="text"
                  value={dbConnection.database}
                  onChange={(e) => setDbConnection({ ...dbConnection, database: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="database_name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Username
                </label>
                <input
                  type="text"
                  value={dbConnection.username}
                  onChange={(e) => setDbConnection({ ...dbConnection, username: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="username"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={dbConnection.password}
                  onChange={(e) => setDbConnection({ ...dbConnection, password: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="password"
                />
              </div>

              {(dbType === 'oracle' || dbType === 'postgresql') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Schema {dbType === 'oracle' && '(Service Name)'}
                  </label>
                  <input
                    type="text"
                    value={dbConnection.schema || ''}
                    onChange={(e) => setDbConnection({ ...dbConnection, schema: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder={dbType === 'oracle' ? 'service_name' : 'schema_name'}
                  />
                </div>
              )}
            </div>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleTestConnection}
                disabled={connectionStatus === 'testing' || !dbConnection.host || !dbConnection.database || !dbConnection.username}
                className="px-4 py-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {connectionStatus === 'testing' ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Testing...</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    <span>Test Connection</span>
                  </>
                )}
              </button>

              {connectionStatus === 'success' && (
                <span className="text-sm text-green-600 dark:text-green-400 flex items-center space-x-1">
                  <CheckCircle className="w-4 h-4" />
                  <span>Connection successful</span>
                </span>
              )}

              {connectionStatus === 'error' && (
                <span className="text-sm text-red-600 dark:text-red-400">
                  Connection failed
                </span>
              )}
            </div>
          </div>
        )}

        {/* Table Selection */}
        {availableTables.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Select Tables to Export
            </h3>

            <div className="space-y-2">
              {availableTables.map((table) => (
                <label
                  key={table}
                  className="flex items-center space-x-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTables.includes(table)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setSelectedTables([...selectedTables, table]);
                      } else {
                        setSelectedTables(selectedTables.filter(t => t !== table));
                      }
                    }}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-900 dark:text-white">{table}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Export Button */}
        <div className="flex items-center justify-end space-x-3">
          <button
            onClick={() => navigate(`/run/${runId}/generate`)}
            className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-900"
          >
            Cancel
          </button>
          <button
            onClick={handleExportToDatabase}
            disabled={isExporting || connectionStatus !== 'success' || selectedTables.length === 0}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
          >
            {isExporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                <span>Export to Database</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportDestinationPage;

